import { useEffect, useRef, useState } from "react";
import { match, P } from "ts-pattern";
import { useRouter } from "@medbillai/navigation/router";
import { Flag, INTAKE_ROUTING_NATERA_DESTINATIONS, stripExperimentVariant } from "@medbillai/utils/flag";
import { loginUrl, nateraIntakeUrl } from "@medbillai/utils/urls";
import { IntakeLanding, LandingCardQuestion } from "../../components/intake";
import { analyticsService } from "../../lib/analytics";
import { createFlowTracker } from "../../lib/analytics/flowEvents";
import { evaluateEnumVariation } from "../../lib/flag";
import { sentryService } from "../../lib/sentry";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import type { Feeling, StressLevel } from "../natera-intake/persona";
import { seedIntakeSessionFromRouting } from "../natera-intake/seedSession";
import { ONBOARDING_VERSION } from "../onboarding-v3/OnboardingFlowV3";
import type { Intent } from "../onboarding-v3/types";
import { copy } from "./copy";
import { routingHero } from "./landingHero";
import { RoutingSaveValidation } from "./RoutingSaveValidation";
import { RoutingValidation } from "./RoutingValidation";

// No flow-engine flow, deliberately: three questions, no persistence, no resume.
type Step = {
  tag: "BillQuestion";
} | {
  tag: "NateraBillQuestion";
} | {
  tag: "StressQuestion";
  nateraBill: boolean;
} | {
  tag: "FeelingQuestion";
  nateraBill: boolean;
  stressLevel: StressLevel;
} | {
  tag: "BillValidation";
  feeling: Feeling;
} | {
  tag: "SaveValidation";
};
const STEP_ORDER = ["BillQuestion", "NateraBillQuestion", "StressQuestion", "FeelingQuestion"] satisfies Step["tag"][];
const BILL_OPTIONS = [{
  id: "bill",
  label: copy.bill.yes
}, {
  id: "save",
  label: copy.bill.save
}] as const;
const NATERA_OPTIONS = [{
  id: "yes",
  label: copy.natera.yes
}, {
  id: "no",
  label: copy.natera.no
}] as const;

// Mapped onto the persona scale, which splits strained/steady at >= 4.
const STRESS_OPTIONS: ReadonlyArray<{
  id: string;
  label: string;
  stressLevel: StressLevel;
}> = [{
  id: "a-lot",
  label: copy.stress.aLot,
  stressLevel: 5
}, {
  id: "a-little",
  label: copy.stress.aLittle,
  stressLevel: 3
}, {
  id: "not-really",
  label: copy.stress.notReally,
  stressLevel: 1
}];

// Labels are routing's own; the ids are the funnel's `Feeling` values because
// the Natera seed (and its persona machinery) consumes them.
const FEELING_OPTIONS: ReadonlyArray<{
  id: Feeling;
  label: string;
}> = [{
  id: "confused",
  label: copy.feeling.confused
}, {
  id: "blindsided",
  label: copy.feeling.blindsided
}, {
  id: "worried",
  label: copy.feeling.worried
}, {
  id: "ready",
  label: copy.feeling.ready
}];
const tracker = createFlowTracker("IntakeRouting", {});
const unexpectedValue = (step: string, value: string) => {
  sentryService.captureException(new Error(`IntakeRouting ${step} received unexpected option id: ${value}`));
};
export function IntakeRoutingScreen() {
  const {
    push
  } = useRouter();
  const setIsSignUpOnboarding = useSignUpOnboardingStore(s => s.setIsSignUpOnboarding);
  const setUserSegmentationData = useSignUpOnboardingStore(s => s.setUserSegmentationData);
  const [step, setStep] = useState<Step>({
    tag: "BillQuestion"
  });
  const answeringRef = useRef(false);
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    // Shared denominator with the control arm; the Bill step-shown event
    // marks the treatment.
    analyticsService.trackEvent("SignUpOnboardingFlowStarted", {
      onboardingVersion: ONBOARDING_VERSION
    });
  }, []);

  // A render cycle after the outcome: emitted together, Amplitude's
  // event_time ties and inverts them.
  useEffect(() => {
    tracker.screen(step.tag);
  }, [step.tag]);

  // `sawPitch` gates the funnel's education screens, so only the exits that
  // actually rendered a validation screen may claim it.
  const exitToSignup = (intent: Intent, {
    sawPitch
  }: {
    sawPitch: boolean;
  }) => {
    setUserSegmentationData({
      features: [intent === "bill" ? "FIX_BILLING_ERRORS" : "SAVE_FUTURE_BILLS", ...(sawPitch ? ["SAW_ROUTING_PITCH"] : [])]
    });
    setIsSignUpOnboarding(true);
    push(loginUrl({
      variant: "signup"
    }));
  };
  const answerBill = (value: string) => {
    const option = BILL_OPTIONS.find(o => o.id === value);
    if (!option) {
      unexpectedValue("BillQuestion", value);
      return;
    }
    tracker.outcome("BillQuestion", "selected", {
      hasBill: option.id === "bill"
    });
    if (option.id === "save") {
      setStep({
        tag: "SaveValidation"
      });
      return;
    }
    setStep({
      tag: "NateraBillQuestion"
    });
  };
  const answerNatera = (value: string) => {
    const option = NATERA_OPTIONS.find(o => o.id === value);
    if (!option) {
      unexpectedValue("NateraBillQuestion", value);
      return;
    }
    tracker.outcome("NateraBillQuestion", "selected", {
      nateraBill: option.id === "yes"
    });
    setStep({
      tag: "StressQuestion",
      nateraBill: option.id === "yes"
    });
  };
  const answerStress = (nateraBill: boolean) => (value: string) => {
    const option = STRESS_OPTIONS.find(o => o.id === value);
    if (!option) {
      unexpectedValue("StressQuestion", value);
      return;
    }
    tracker.outcome("StressQuestion", "selected", {
      stressLevel: option.stressLevel
    });
    setStep({
      tag: "FeelingQuestion",
      nateraBill,
      stressLevel: option.stressLevel
    });
  };
  const answerFeeling = (nateraBill: boolean, stressLevel: StressLevel) => async (value: string) => {
    const option = FEELING_OPTIONS.find(o => o.id === value);
    if (!option) {
      unexpectedValue("FeelingQuestion", value);
      return;
    }
    // The buttons stay live through the seed write and the navigation, so
    // a double tap would re-run everything below; the second tap drops here.
    // Never reset: every branch below either navigates away or lands on a
    // validation screen with no route back to this question.
    if (answeringRef.current) {
      return;
    }
    answeringRef.current = true;
    const feeling = option.id;
    tracker.outcome("FeelingQuestion", "selected", {
      feeling
    });

    // Evaluation is enrollment — hoisting this out of the natera-bill
    // branch enrolls members who can't receive either arm.
    const destinationVariant = nateraBill ? evaluateEnumVariation(INTAKE_ROUTING_NATERA_DESTINATIONS, Flag.INTAKE_ROUTING_NATERA_DESTINATION, "natera-intake") : "onboarding";
    const destination = stripExperimentVariant(destinationVariant);
    if (nateraBill) {
      analyticsService.trackEvent("IntakeRoutingNateraArmAssigned", {
        variant: destinationVariant,
        destination: destination === "natera-intake" ? destination : "signup"
      });
    }
    if (destination === "natera-intake") {
      try {
        // The redirect must not outrun the session write the funnel resumes
        // from. A failed write (private-mode/webview storage) degrades to the
        // funnel starting from its own beginning — never a stranded member.
        await seedIntakeSessionFromRouting({
          stressLevel,
          feeling
        });
      } catch (error) {
        sentryService.captureException(error);
      }
      push(nateraIntakeUrl());
      return;
    }
    if (nateraBill) {
      exitToSignup("bill", {
        sawPitch: false
      });
      return;
    }
    setStep({
      tag: "BillValidation",
      feeling
    });
  };
  const goToStep = (targetTag: (typeof STEP_ORDER)[number]) => {
    tracker.back(step.tag);
    match([targetTag, step]).with(["StressQuestion", {
      tag: "FeelingQuestion"
    }], ([_, {
      nateraBill
    }]) => {
      setStep({
        tag: "StressQuestion",
        nateraBill
      });
    }).with([P.union("NateraBillQuestion", "BillQuestion"), {
      tag: P._
    }], ([tag, _]) => {
      setStep({
        tag
      });
    }).otherwise(() => {
      // noop
    });
  };
  if (step.tag === "SaveValidation") {
    return <RoutingSaveValidation onContinue={() => {
      tracker.continue("SaveValidation");
      exitToSignup("save", {
        sawPitch: true
      });
    }} />;
  }
  if (step.tag === "BillValidation") {
    return <RoutingValidation feeling={step.feeling} onContinue={() => {
      tracker.continue("BillValidation");
      exitToSignup("bill", {
        sawPitch: true
      });
    }} />;
  }
  const question = match(step).with({
    tag: "BillQuestion"
  }, () => ({
    title: copy.bill.title,
    options: BILL_OPTIONS,
    onChange: answerBill
  })).with({
    tag: "NateraBillQuestion"
  }, () => ({
    title: copy.natera.title,
    options: NATERA_OPTIONS,
    onChange: answerNatera
  })).with({
    tag: "StressQuestion"
  }, ({
    nateraBill
  }) => ({
    title: copy.stress.title,
    options: STRESS_OPTIONS,
    onChange: answerStress(nateraBill)
  })).with({
    tag: "FeelingQuestion"
  }, ({
    nateraBill,
    stressLevel
  }) => ({
    title: copy.feeling.title,
    options: FEELING_OPTIONS,
    onChange: answerFeeling(nateraBill, stressLevel)
  })).exhaustive();
  return <IntakeLanding hero={routingHero} steps={STEP_ORDER} current={step.tag} onStepPress={goToStep} data-sentry-element="IntakeLanding" data-sentry-component="IntakeRoutingScreen" data-sentry-source-file="IntakeRoutingScreen.tsx">
      <LandingCardQuestion title={question.title} options={question.options.map(({
      id,
      label
    }) => ({
      id,
      label,
      testID: `intake-routing.${step.tag.toLowerCase()}-${id}`,
      onPress: () => {
        void question.onChange(id);
      }
    }))} data-sentry-element="LandingCardQuestion" data-sentry-source-file="IntakeRoutingScreen.tsx" />
    </IntakeLanding>;
}